import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Checkbox } from './components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Heart, Home, Calculator, Phone, Mail, Euro, Building2, Users, CreditCard, Eye, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import Logo from './components/Logo';
import ErrorBoundary from './components/ErrorBoundary';
// 🚀 Lazy-load des écrans lourds (CRM = 1 Mo, pages factures) — réduit le bundle initial
const CRM = lazy(() => import('./components/CRM'));
const ProtectedCRM = lazy(() => import('./components/ProtectedCRM'));
const ProtectedInvoices = lazy(() => import('./components/ProtectedInvoices'));
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const CreateInvoice = lazy(() => import('./pages/invoices/CreateInvoice'));
const InvoiceView = lazy(() => import('./pages/invoices/InvoiceView'));
const EditInvoice = lazy(() => import('./pages/invoices/EditInvoice'));
const ClientSynthesis = lazy(() => import('./pages/invoices/ClientSynthesis'));
const AnnualReport = lazy(() => import('./pages/invoices/AnnualReport'));
const ApporteurPortal = lazy(() => import('./pages/parrainage/ApporteurPortal'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PublicLotPage = lazy(() => import('./pages/PublicLotPage'));
const LoveliaLanding = lazy(() => import('./pages/LoveliaLanding'));
const LegalPage = lazy(() => import('./components/LegalPage'));
import { AuthProvider } from './contexts/AuthContext';
import { t } from './translations';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant principal du formulaire d'estimation

const EstimationForm = () => {
  // Force scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // État pour la langue
  const [language, setLanguage] = useState('fr');

  const [formData, setFormData] = useState({
    recommande: '',
    nom_recommandation: '',
    civilite: '',
    nom: '',
    prenom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    date_naissance_monsieur: '',
    date_naissance_madame: '',
    situation_familiale: '',
    nombre_enfants: 0,
    loyer_actuel: 0,
    salaire_brut_monsieur: 0,
    salaire_net_monsieur: 0,
    primes_monsieur: 0,
    statut_monsieur: '',
    profession_monsieur: '',
    lieu_travail_monsieur: '',
    salaire_brut_madame: 0,
    salaire_net_madame: 0,
    primes_madame: 0,
    statut_madame: '',
    profession_madame: '',
    lieu_travail_madame: '',
    revenus_caf: 0,
    banque_actuelle: '',
    credit_en_cours: 'non',
    organisme_credit: '',
    mensualite_credit: 0,
    montant_restant_credit: 0,
    apport_projet: 0,
    mensualite_souhaitee: 0,
    type_bien: '',
    nombre_chambres: '',
    ville_choix_1: '',
    ville_choix_2: '',
    ville_choix_3: '',
    description_bien: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const totalSteps = 4;

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumericFocus = (name) => {
    // Effacer le 0 par défaut quand on clique sur un champ numérique
    if (formData[name] === 0) {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNumericBlur = (name, value) => {
    // Remettre 0 si le champ est vide
    const numericValue = value === '' ? 0 : parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
  };

  const generateWhatsAppMessage = (data) => {
    const totalRevenus = (
      data.salaire_brut_monsieur + data.salaire_net_monsieur + data.primes_monsieur +
      data.salaire_brut_madame + data.salaire_net_madame + data.primes_madame +
      data.revenus_caf
    );
    
    const message = `🏠 NOUVELLE DEMANDE D'ESTIMATION IMMOBILIÈRE

📋 CLIENT:
• Nom: ${data.nom} ${data.prenom}
• Téléphone: ${data.telephone}
• Email: ${data.email}
• Adresse: ${data.adresse}

👨‍👩‍👧‍👦 SITUATION:
• Situation familiale: ${data.situation_familiale}
• Nombre d'enfants: ${data.nombre_enfants}
• Loyer actuel: ${data.loyer_actuel.toLocaleString()}€

💰 REVENUS MENSUELS:
• Monsieur: ${data.salaire_net_monsieur.toLocaleString()}€ net + ${data.primes_monsieur.toLocaleString()}€ primes (${data.statut_monsieur || 'Non précisé'})
• Madame: ${data.salaire_net_madame.toLocaleString()}€ net + ${data.primes_madame.toLocaleString()}€ primes (${data.statut_madame || 'Non précisé'})
• CAF: ${data.revenus_caf.toLocaleString()}€
• TOTAL: ${totalRevenus.toLocaleString()}€/mois

🏦 SITUATION FINANCIÈRE:
• Banque: ${data.banque_actuelle}
• Crédits: ${data.credit_en_cours}
• Apport: ${data.apport_projet.toLocaleString()}€
• Mensualité souhaitée: ${data.mensualite_souhaitee.toLocaleString()}€

🏡 PROJET IMMOBILIER:
• Type: ${data.type_bien}
• Chambres: ${data.nombre_chambres}
• Villes: ${data.villes_souhaitees}
• Critères: ${data.description_bien}

📅 Demande du ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`;

    return encodeURIComponent(message);
  };

  const openWhatsApp = () => {
    if (submittedData) {
      const message = generateWhatsAppMessage(submittedData);
      const whatsappUrl = `https://wa.me/33647521453?text=${message}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  // Écran de succès après soumission
  if (submissionSuccess && submittedData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <Logo size="large" className="mx-auto mb-8" />
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-16 w-16 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-2xl text-green-800">Demande envoyée avec succès !</CardTitle>
                <CardDescription className="text-green-700 mt-2">
                  Votre demande d'estimation a été transmise à Teddy FRESILLON
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 text-center">
                <div className="bg-white p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2">Résumé de votre demande</h3>
                  <div className="text-sm text-green-700 space-y-1">
                    <p><strong>Client :</strong> {submittedData.prenom} {submittedData.nom}</p>
                    <p><strong>Contact :</strong> {submittedData.telephone}</p>
                    <p><strong>Projet :</strong> {submittedData.type_bien} - {submittedData.nombre_chambres}</p>
                    <p><strong>Villes :</strong> {submittedData.villes_souhaitees}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-slate-600">
                    📧 Un email détaillé a été envoyé à Teddy FRESILLON<br/>
                    📱 Vous pouvez également lui envoyer un message WhatsApp
                  </p>
                  
                  <Button
                    onClick={openWhatsApp}
                    className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 text-lg"
                    size="lg"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Envoyer sur WhatsApp
                  </Button>
                  
                  <p className="text-xs text-slate-500">
                    Le message sera automatiquement pré-rempli avec vos informations
                  </p>
                </div>

                <div className="border-t border-green-200 pt-4">
                  <p className="text-sm text-green-700">
                    <strong>Prochaines étapes :</strong><br/>
                    Teddy FRESILLON vous contactera sous 24h pour analyser votre dossier<br/>
                    et vous proposer des biens correspondant à vos critères.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.recommande && formData.civilite && formData.nom && formData.prenom && formData.adresse && formData.code_postal && formData.ville && formData.telephone && formData.email;
      case 2:
        // Salaire net et statut obligatoires, lieu de travail optionnel
        let requiredFields = formData.situation_familiale;
        
        // Vérifier les champs obligatoires selon la situation
        if (shouldShowMonsieurFields()) {
          requiredFields = requiredFields && formData.salaire_net_monsieur > 0 && formData.statut_monsieur;
        }
        if (shouldShowMadameFields()) {
          requiredFields = requiredFields && formData.salaire_net_madame > 0 && formData.statut_madame;  
        }
        
        return requiredFields;
      case 3:
        // Validation pour l'étape 3 - apport_projet n'est plus obligatoire
        const basicFields = formData.credit_en_cours;
        if (formData.credit_en_cours === 'oui') {
          return basicFields && formData.organisme_credit && formData.mensualite_credit > 0;
        }
        return basicFields;
      case 4:
        return formData.type_bien && formData.nombre_chambres && formData.ville_choix_1;
      default:
        return true;
    }
  };

  // Logique pour déterminer quels champs afficher selon civilité et situation
  const shouldShowMonsieurFields = () => {
    const situationCouple = ['marie', 'pacs', 'concubinage'].includes(formData.situation_familiale);
    const situationSeul = ['celibataire', 'divorce', 'veuf'].includes(formData.situation_familiale);
    
    if (situationCouple) return true; // Toujours afficher pour les couples
    if (situationSeul && formData.civilite === 'Monsieur') return true;
    return false;
  };

  const shouldShowMadameFields = () => {
    const situationCouple = ['marie', 'pacs', 'concubinage'].includes(formData.situation_familiale);
    const situationSeul = ['celibataire', 'divorce', 'veuf'].includes(formData.situation_familiale);
    
    if (situationCouple) return true; // Toujours afficher pour les couples
    if (situationSeul && formData.civilite === 'Madame') return true;
    return false;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      // Scroll vers le haut lors du changement d'étape
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error('Veuillez remplir tous les champs obligatoires');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll vers le haut lors du changement d'étape
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/estimation`, formData);
      
      if (response.status === 200) {
        toast.success('Votre demande d\'estimation a été envoyée avec succès ! 🏠');
        toast.success('Vous recevrez une réponse sous 24h par email et WhatsApp');
        
        // Stocker les données pour WhatsApp et afficher le succès
        setSubmittedData(formData);
        setSubmissionSuccess(true);
        
        // Reset form après un délai
        setTimeout(() => {
          setFormData({
            recommande: '',
            nom_recommandation: '',
            civilite: '',
            nom: '',
            prenom: '',
            adresse: '',
            code_postal: '',
            ville: '',
            telephone: '',
            email: '',
            date_naissance_monsieur: '',
            date_naissance_madame: '',
            situation_familiale: '',
            nombre_enfants: 0,
            loyer_actuel: 0,
            salaire_brut_monsieur: 0,
            salaire_net_monsieur: 0,
            primes_monsieur: 0,
            statut_monsieur: '',
            profession_monsieur: '',
            lieu_travail_monsieur: '',
            salaire_brut_madame: 0,
            salaire_net_madame: 0,
            primes_madame: 0,
            statut_madame: '',
            profession_madame: '',
            lieu_travail_madame: '',
            revenus_caf: 0,
            banque_actuelle: '',
            credit_en_cours: 'non',
            organisme_credit: '',
            mensualite_credit: 0,
            montant_restant_credit: 0,
            apport_projet: 0,
            mensualite_souhaitee: 0,
            type_bien: '',
            nombre_chambres: '',
            ville_choix_1: '',
            ville_choix_2: '',
            ville_choix_3: '',
            mensualite_souhaitee: 0,
            type_bien: '',
            nombre_chambres: '',
            villes_souhaitees: '',
            description_bien: ''
          });
          setCurrentStep(1);
          setSubmissionSuccess(false);
          setSubmittedData(null);
        }, 10000); // Garder l'écran de succès 10 secondes
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-red-600 mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('personalInfo', language)}</CardTitle>
              </div>
              <CardDescription>{t('personalInfoDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="recommande" className="text-slate-700 font-medium">{t('recommended', language)} *</Label>
                <Select onValueChange={(value) => handleInputChange('recommande', value)}>
                  <SelectTrigger className="border-slate-300 focus:border-red-500">
                    <SelectValue placeholder={t('selectAnswer', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">{t('no', language)}</SelectItem>
                    <SelectItem value="oui">{t('yes', language)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.recommande === 'oui' && (
                <div className="space-y-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label htmlFor="nom_recommandation" className="text-slate-700 font-medium">{t('recommendedByFull', language)} *</Label>
                  <Input
                    id="nom_recommandation"
                    value={formData.nom_recommandation}
                    onChange={(e) => handleInputChange('nom_recommandation', e.target.value)}
                    className="border-slate-300 focus:border-red-500"
                    placeholder={t('recommendedByFull', language)}
                    required
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="civilite" className="text-slate-700 font-medium">{t('civility', language)} *</Label>
                <Select onValueChange={(value) => handleInputChange('civilite', value)}>
                  <SelectTrigger className="border-slate-300 focus:border-red-500">
                    <SelectValue placeholder={t('selectCivility', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monsieur">{t('mr', language)}</SelectItem>
                    <SelectItem value="Madame">{t('mrs', language)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-slate-700 font-medium">{t('lastName', language)} *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => handleInputChange('nom', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('lastNamePlaceholder', language)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="text-slate-700 font-medium">{t('firstName', language)} *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => handleInputChange('prenom', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('firstNamePlaceholder', language)}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="adresse" className="text-slate-700 font-medium">{t('address', language)} *</Label>
                <Input
                  id="adresse"
                  value={formData.adresse}
                  onChange={(e) => handleInputChange('adresse', e.target.value)}
                  className="border-slate-300 focus:border-blue-500"
                  placeholder={t('addressPlaceholder', language)}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code_postal" className="text-slate-700 font-medium">{t('postalCode', language)} *</Label>
                  <Input
                    id="code_postal"
                    value={formData.code_postal}
                    onChange={(e) => handleInputChange('code_postal', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('postalCodePlaceholder', language)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ville" className="text-slate-700 font-medium">{t('city', language)} *</Label>
                  <Input
                    id="ville"
                    value={formData.ville}
                    onChange={(e) => handleInputChange('ville', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('cityPlaceholder', language)}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone" className="text-slate-700 font-medium flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    {t('phone', language)} *
                  </Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => handleInputChange('telephone', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('phonePlaceholder', language)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    {t('email', language)} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('emailPlaceholder', language)}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_naissance_monsieur" className="text-slate-700 font-medium">{t('birthDateMr', language)}</Label>
                  <Input
                    id="date_naissance_monsieur"
                    type="date"
                    value={formData.date_naissance_monsieur}
                    onChange={(e) => handleInputChange('date_naissance_monsieur', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('birthDatePlaceholder', language)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_naissance_madame" className="text-slate-700 font-medium">{t('birthDateMrs', language)}</Label>
                  <Input
                    id="date_naissance_madame"
                    type="date"
                    value={formData.date_naissance_madame}
                    onChange={(e) => handleInputChange('date_naissance_madame', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Home className="h-8 w-8 text-red-600 mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('familySituationAndIncome', language)}</CardTitle>
              </div>
              <CardDescription>{t('familySituationAndIncomeDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="situation_familiale" className="text-slate-700 font-medium">{t('maritalStatus', language)} *</Label>
                  <Select onValueChange={(value) => handleInputChange('situation_familiale', value)}>
                    <SelectTrigger className="border-slate-300 focus:border-blue-500">
                      <SelectValue placeholder={t('selectMaritalStatus', language)} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celibataire">Célibataire</SelectItem>
                      <SelectItem value="marie">Marié(e)</SelectItem>
                      <SelectItem value="pacs">Pacsé(e)</SelectItem>
                      <SelectItem value="concubinage">Concubinage</SelectItem>
                      <SelectItem value="divorce">Divorcé(e)</SelectItem>
                      <SelectItem value="veuf">Veuf(ve)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nombre_enfants" className="text-slate-700 font-medium">Nombre d'enfants</Label>
                  <Input
                    id="nombre_enfants"
                    type="number"
                    min="0"
                    max="20"
                    value={formData.nombre_enfants === 0 ? '' : formData.nombre_enfants}
                    onFocus={() => handleNumericFocus('nombre_enfants')}
                    onBlur={(e) => handleNumericBlur('nombre_enfants', e.target.value)}
                    onChange={(e) => handleInputChange('nombre_enfants', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="loyer_actuel" className="text-slate-700 font-medium flex items-center">
                  <Euro className="h-4 w-4 mr-1" />
                  {t('currentRentLabel', language)}
                </Label>
                <Input
                  id="loyer_actuel"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.loyer_actuel === 0 ? '' : formData.loyer_actuel}
                  onFocus={() => handleNumericFocus('loyer_actuel')}
                  onBlur={(e) => handleNumericBlur('loyer_actuel', e.target.value)}
                  onChange={(e) => handleInputChange('loyer_actuel', e.target.value)}
                  className="border-slate-300 focus:border-blue-500"
                  placeholder="0"
                />
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('netMonthlyIncome', language)}</h3>
                
                <div className="grid grid-cols-1 gap-6">
                  {/* Revenus Monsieur - Affiché conditionnellement */}
                  {shouldShowMonsieurFields() && (
                    <div className="space-y-4 border-b border-slate-200 pb-4">
                      <Label className="text-slate-700 font-medium text-base">{t('maleIncomeLabel', language)}</Label>
                      
                      {/* Nouveaux champs profession et lieu de travail */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('profession', language)} Monsieur *</Label>
                          <Input
                            value={formData.profession_monsieur}
                            onChange={(e) => handleInputChange('profession_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('professionPlaceholder', language)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">{t('workplace', language)} (optionnel)</Label>
                          <Input
                            value={formData.lieu_travail_monsieur}
                            onChange={(e) => handleInputChange('lieu_travail_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('workplacePlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('grossSalary', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_brut_monsieur === 0 ? '' : formData.salaire_brut_monsieur}
                            onFocus={() => handleNumericFocus('salaire_brut_monsieur')}
                            onBlur={(e) => handleNumericBlur('salaire_brut_monsieur', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_brut_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('grossSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('netSalary', language)} *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_net_monsieur === 0 ? '' : formData.salaire_net_monsieur}
                            onFocus={() => handleNumericFocus('salaire_net_monsieur')}
                            onBlur={(e) => handleNumericBlur('salaire_net_monsieur', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_net_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('netSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('bonuses', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.primes_monsieur === 0 ? '' : formData.primes_monsieur}
                            onFocus={() => handleNumericFocus('primes_monsieur')}
                            onBlur={(e) => handleNumericBlur('primes_monsieur', e.target.value)}
                            onChange={(e) => handleInputChange('primes_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('bonusesPlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-sm text-slate-600">{t('status', language)} *</Label>
                        <RadioGroup
                          value={formData.statut_monsieur}
                          onValueChange={(value) => handleInputChange('statut_monsieur', value)}
                          className="flex flex-row space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDI" id="cdi_m" />
                            <Label htmlFor="cdi_m" className="text-sm">CDI</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDD" id="cdd_m" />
                            <Label htmlFor="cdd_m" className="text-sm">CDD</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Autres" id="autres_m" />
                            <Label htmlFor="autres_m" className="text-sm">Autres</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                  
                  {/* Revenus Madame - Affiché conditionnellement */}
                  {shouldShowMadameFields() && (
                    <div className="space-y-4 border-b border-slate-200 pb-4">
                      <Label className="text-slate-700 font-medium text-base">{t('femaleIncomeLabel', language)}</Label>
                      
                      {/* Nouveaux champs profession et lieu de travail */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('profession', language)} Madame *</Label>
                          <Input
                            value={formData.profession_madame}
                            onChange={(e) => handleInputChange('profession_madame', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('professionPlaceholder', language)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">{t('workplace', language)} (optionnel)</Label>
                          <Input
                            value={formData.lieu_travail_madame}
                            onChange={(e) => handleInputChange('lieu_travail_madame', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('workplacePlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('grossSalary', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_brut_madame === 0 ? '' : formData.salaire_brut_madame}
                            onFocus={() => handleNumericFocus('salaire_brut_madame')}
                            onBlur={(e) => handleNumericBlur('salaire_brut_madame', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_brut_madame', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('grossSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('netSalary', language)} *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_net_madame === 0 ? '' : formData.salaire_net_madame}
                            onFocus={() => handleNumericFocus('salaire_net_madame')}
                            onBlur={(e) => handleNumericBlur('salaire_net_madame', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_net_madame', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('netSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('bonuses', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.primes_madame === 0 ? '' : formData.primes_madame}
                            onFocus={() => handleNumericFocus('primes_madame')}
                            onBlur={(e) => handleNumericBlur('primes_madame', e.target.value)}
                            onChange={(e) => handleInputChange('primes_madame', e.target.value)}
                            className="border-slate-300 focus:border-red-500"
                            placeholder={t('bonusesPlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-sm text-slate-600">{t('status', language)} *</Label>
                        <RadioGroup
                          value={formData.statut_madame}
                          onValueChange={(value) => handleInputChange('statut_madame', value)}
                          className="flex flex-row space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDI" id="cdi_f" />
                            <Label htmlFor="cdi_f" className="text-sm">CDI</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDD" id="cdd_f" />
                            <Label htmlFor="cdd_f" className="text-sm">CDD</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Autres" id="autres_f" />
                            <Label htmlFor="autres_f" className="text-sm">Autres</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                  
                  {/* Revenus CAF */}
                  <div className="space-y-2">
                    <Label htmlFor="revenus_caf" className="text-slate-700 font-medium">{t('cafIncomeLabel', language)}</Label>
                    <Input
                      id="revenus_caf"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.revenus_caf === 0 ? '' : formData.revenus_caf}
                      onFocus={() => handleNumericFocus('revenus_caf')}
                      onBlur={(e) => handleNumericBlur('revenus_caf', e.target.value)}
                      onChange={(e) => handleInputChange('revenus_caf', e.target.value)}
                      className="border-slate-300 focus:border-red-500"
                      placeholder={t('cafIncomePlaceholder', language)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <CreditCard className="h-8 w-8 text-red-600 mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('financialSituation', language)}</CardTitle>
              </div>
              <CardDescription>{t('financialSituationDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="banque_actuelle" className="text-slate-700 font-medium">{t('yourBanks', language)}</Label>
                <Input
                  id="banque_actuelle"
                  value={formData.banque_actuelle}
                  onChange={(e) => handleInputChange('banque_actuelle', e.target.value)}
                  className="border-slate-300 focus:border-blue-500"
                  placeholder={t('yourBanksPlaceholder', language)}
                />
              </div>
              
              <div className="space-y-4">
                <Label htmlFor="credit_en_cours" className="text-slate-700 font-medium">{t('existingCreditQuestion', language)} *</Label>
                <Select onValueChange={(value) => handleInputChange('credit_en_cours', value)}>
                  <SelectTrigger className="border-slate-300 focus:border-blue-500">
                    <SelectValue placeholder={t('selectAnswer', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">{t('no', language)}</SelectItem>
                    <SelectItem value="oui">{t('yes', language)}</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.credit_en_cours === 'oui' && (
                  <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-slate-800">{t('creditInfo', language)}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="organisme_credit" className="text-slate-700 font-medium">{t('creditOrganizationQuestion', language)} *</Label>
                      <Input
                        id="organisme_credit"
                        value={formData.organisme_credit}
                        onChange={(e) => handleInputChange('organisme_credit', e.target.value)}
                        className="border-slate-300 focus:border-blue-500"
                        placeholder={t('creditOrganizationPlaceholder', language)}
                        required={formData.credit_en_cours === 'oui'}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mensualite_credit" className="text-slate-700 font-medium flex items-center">
                          <Euro className="h-4 w-4 mr-1" />
                          {t('monthlyPaymentQuestion', language)} *
                        </Label>
                        <Input
                          id="mensualite_credit"
                          type="number"
                          min="0"
                          step="10"
                          value={formData.mensualite_credit === 0 ? '' : formData.mensualite_credit}
                          onFocus={() => handleNumericFocus('mensualite_credit')}
                          onBlur={(e) => handleNumericBlur('mensualite_credit', e.target.value)}
                          onChange={(e) => handleInputChange('mensualite_credit', e.target.value)}
                          className="border-slate-300 focus:border-blue-500"
                          placeholder={t('monthlyPaymentPlaceholder', language)}
                          required={formData.credit_en_cours === 'oui'}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="montant_restant_credit" className="text-slate-700 font-medium flex items-center">
                          <Euro className="h-4 w-4 mr-1" />
                          {t('remainingAmountQuestion', language)}
                        </Label>
                        <Input
                          id="montant_restant_credit"
                          type="number"
                          min="0"
                          step="1000"
                          value={formData.montant_restant_credit === 0 ? '' : formData.montant_restant_credit}
                          onFocus={() => handleNumericFocus('montant_restant_credit')}
                          onBlur={(e) => handleNumericBlur('montant_restant_credit', e.target.value)}
                          onChange={(e) => handleInputChange('montant_restant_credit', e.target.value)}
                          className="border-slate-300 focus:border-blue-500"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="apport_projet" className="text-slate-700 font-medium flex items-center">
                    <Euro className="h-4 w-4 mr-1" />
                    {t('downPayment', language)}
                  </Label>
                  <Input
                    id="apport_projet"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.apport_projet === 0 ? '' : formData.apport_projet}
                    onFocus={() => handleNumericFocus('apport_projet')}
                    onBlur={(e) => handleNumericBlur('apport_projet', e.target.value)}
                    onChange={(e) => handleInputChange('apport_projet', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder={t('downPaymentPlaceholder', language)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mensualite_souhaitee" className="text-slate-700 font-medium flex items-center">
                    <Calculator className="h-4 w-4 mr-1" />
                    {t('desiredMonthlyRepayment', language)}
                  </Label>
                  <Input
                    id="mensualite_souhaitee"
                    type="number"
                    min="0"
                    step="50"
                    value={formData.mensualite_souhaitee === 0 ? '' : formData.mensualite_souhaitee}
                    onFocus={() => handleNumericFocus('mensualite_souhaitee')}
                    onBlur={(e) => handleNumericBlur('mensualite_souhaitee', e.target.value)}
                    onChange={(e) => handleInputChange('mensualite_souhaitee', e.target.value)}
                    className="border-slate-300 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-red-600 mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('yourRealEstateProject', language)}</CardTitle>
              </div>
              <CardDescription>{t('describePropertyYouAreLookingFor', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="type_bien" className="text-slate-700 font-medium">{t('propertyTypeLabel', language)} *</Label>
                  <Select onValueChange={(value) => handleInputChange('type_bien', value)}>
                    <SelectTrigger className="border-slate-300 focus:border-red-500">
                      <SelectValue placeholder={t('selectPropertyTypePlaceholder', language)} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartement">{t('apartment', language)}</SelectItem>
                      <SelectItem value="maison">{t('house', language)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nombre_chambres" className="text-slate-700 font-medium">{t('bedroomsLabel', language)} *</Label>
                  <Select onValueChange={(value) => handleInputChange('nombre_chambres', value)}>
                    <SelectTrigger className="border-slate-300 focus:border-red-500">
                      <SelectValue placeholder={t('bedroomsPlaceholder', language)} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 pièce</SelectItem>
                      <SelectItem value="2">2 pièces</SelectItem>
                      <SelectItem value="3">3 pièces</SelectItem>
                      <SelectItem value="4">4 pièces</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ville_choix_1" className="text-slate-700 font-medium">{t('citiesOrDepartments', language)} *</Label>
                  <Textarea
                    id="ville_choix_1"
                    value={formData.ville_choix_1}
                    onChange={(e) => handleInputChange('ville_choix_1', e.target.value)}
                    className="border-slate-300 focus:border-red-500"
                    placeholder={t('citiesPlaceholder', language)}
                    rows={3}
                    autoFocus={false}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description_bien" className="text-slate-700 font-medium">{t('propertyDescription', language)}</Label>
                <Textarea
                  id="description_bien"
                  value={formData.description_bien}
                  onChange={(e) => handleInputChange('description_bien', e.target.value)}
                  className="border-slate-300 focus:border-red-500 min-h-[150px]"
                  placeholder="Détaillez vos critères : proximité transports, présence de balcon/terrasse, parking, étage souhaité, exposition, prestations particulières, etc."
                />
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-900 mb-2">Récapitulatif de votre demande</h3>
                <div className="text-sm text-red-800 space-y-1">
                  <p><strong>Client :</strong> {formData.prenom} {formData.nom}</p>
                  <p><strong>Contact :</strong> {formData.telephone} - {formData.email}</p>
                  <p><strong>Situation :</strong> {formData.situation_familiale}</p>
                  <p><strong>Revenus totaux :</strong> {(formData.salaire_brut_monsieur + formData.salaire_net_monsieur + formData.primes_monsieur + formData.salaire_brut_madame + formData.salaire_net_madame + formData.primes_madame + formData.revenus_caf).toLocaleString()} €</p>
                  <p><strong>Apport :</strong> {formData.apport_projet.toLocaleString()} €</p>
                  <p><strong>Bien recherché :</strong> {formData.type_bien} - {formData.nombre_chambres} chambre(s)</p>
                  <p><strong>Villes/Départements :</strong> {formData.ville_choix_1 || 'Non précisé'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Sélecteur de langue en haut à droite */}
      <div className="absolute top-4 right-4 z-50">
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-[180px] bg-white shadow-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fr">🇫🇷 Français</SelectItem>
            <SelectItem value="en">🇬🇧 English</SelectItem>
            <SelectItem value="pt">🇵🇹 Português</SelectItem>
            <SelectItem value="es">🇪🇸 Español</SelectItem>
            <SelectItem value="it">🇮🇹 Italiano</SelectItem>
            <SelectItem value="ro">🇷🇴 Română</SelectItem>
            <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        {/* Header avec Logo */}
        <div className="text-center mb-12">
          <Logo size="large" className="mx-auto mb-8" />
          <p className="text-xl text-slate-700 max-w-4xl mx-auto leading-relaxed font-medium">
            {t('headerTitle', language)}
          </p>
          <p className="text-xs text-slate-500 mt-3">
            (LOI n° 2018-493 du 20 juin 2018 relative à la protection des données personnelles)
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">{t('stepProgress', language)} {currentStep} {t('of', language)} {totalSteps}</span>
            <span className="text-sm font-medium text-slate-600">{Math.round((currentStep / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-red-600 to-red-700 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit}>
            {renderStep()}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-2 border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                {t('previous', language)}
              </Button>
              
              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                >
                  {t('next', language)}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="px-8 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                >
                  {isLoading ? t('submit', language) + '...' : t('submit', language)}
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-slate-200">
          <Logo size="small" className="mx-auto mb-4" />
          
          {/* Informations légales de l'entreprise */}
          <div className="max-w-4xl mx-auto mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">LOVELIA — Immobilier neuf</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div className="space-y-1">
                <p><strong>Adresse :</strong></p>
                <p>1 place Paul Verlaine</p>
                <p>92100 BOULOGNE BILLANCOURT</p>
              </div>
              
              <div className="space-y-1">
                <p><strong>Contact :</strong></p>
                <p>Téléphone : 06.47.52.14.53</p>
                <p>Mail : teddy.fresillon@lovelia-immo.fr</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-300 text-xs text-slate-500 space-y-1">
              <p><strong>Siret :</strong> 750 089 732 - R.C.S. Nanterre</p>
              <p><strong>Transaction sur immeubles et fonds de commerces</strong></p>
              <p>Carte professionnelle n° : CPI92012025000000108 délivrée par la CCI de Paris Île-de-France</p>
            </div>
          </div>
          
          <p className="text-slate-500">
            © 2024 Teddy FRESILLON - Votre conseiller immobilier de confiance
          </p>
          
          {/* Accès admin discret - invisible pour les clients */}
          <div className="mt-2">
            <a 
              href="/admin" 
              className="text-xs text-slate-300 hover:text-slate-400 transition-colors"
              style={{ fontSize: '10px', opacity: 0.3 }}
            >
              •
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center">
              <div className="inline-block w-10 h-10 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500">Chargement…</p>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<LoveliaLanding />} />
            <Route path="/admin" element={<ProtectedCRM />} />
            <Route path="/crm" element={<ProtectedCRM />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/factures" element={<ProtectedInvoices><InvoiceList /></ProtectedInvoices>} />
            <Route path="/factures/new" element={<ProtectedInvoices><CreateInvoice /></ProtectedInvoices>} />
            <Route path="/factures/synthese" element={<ProtectedInvoices><ClientSynthesis /></ProtectedInvoices>} />
            <Route path="/factures/rapport" element={<ProtectedInvoices><AnnualReport /></ProtectedInvoices>} />
            <Route path="/factures/:id" element={<ProtectedInvoices><InvoiceView /></ProtectedInvoices>} />
            <Route path="/factures/:id/edit" element={<ProtectedInvoices><EditInvoice /></ProtectedInvoices>} />
            {/* Portail public Parrainage — pas de ProtectedCRM, accès libre */}
            <Route path="/parrainage/*" element={<ApporteurPortal />} />
            {/* 🌐 Page publique de partage d'un lot Miizy — accès libre, token unique */}
            <Route path="/lot/:token" element={<PublicLotPage />} />
            {/* 📄 Pages légales publiques (LCEN / RGPD) */}
            <Route path="/mentions-legales" element={<LegalPage slug="mentions-legales" />} />
            <Route path="/politique-confidentialite" element={<LegalPage slug="politique-confidentialite" />} />
            <Route path="/cgu" element={<LegalPage slug="cgu" />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;